import React, { useEffect, useState } from 'react';
import { ImageBox } from '../../../components/BoxedImage';
import { ComplianceLayerLegend } from '../../../components/ComplianceLayerLegend';
import { ImageViewer } from '../../../components/ImageViewer';
import { BBox, BoxAction, CameraPog, PogBBox } from '../../../types';
import { COMPLIANCE_COLOR } from '../../store-details/components/PlanogramComplianceControlCenter';

interface CameraImageViewerProps {
  // Camera image props
  preloadedImage?: string;
  fallbackPogBoxColor?: string;
  selectedPogBoxColor?: string;
  highlightedIlItemId?: number;
  filteredBboxes: BBox[];
  boxActions: BoxAction[];
  productRecognitionDetections: ImageBox[];
  planogramComplianceActions: ImageBox[];
  showProductImages: boolean;
  onBoxClick: (bbox: BBox) => void;
  onProductRecognitionClick?: (detection: ImageBox, detectionIndex: number) => void;
  onProductRecognitionRightClick?: (detection: ImageBox | PogBBox) => void;
  onPlanogramComplianceClick?: (detection: ImageBox) => void;
  titleActions?: React.ReactNode;
  onBoxesSelected?: (selectedBboxes: BBox[]) => void;

  // Edit mode props
  editMode?: boolean;
  selectedShape?: ImageBox | null;
  selectedDetectionIndex?: number | null;
  onEditModeEnable?: () => void;
  onRemoveShape?: () => void;
  onSaveChanges?: () => void;
  onCancelChanges?: () => void;
  onCornerDragStart?: (event: React.MouseEvent | React.TouchEvent, cornerIndex: number) => void;
  onDetectionEdited?: (updatedDetection: ImageBox) => void;
  onMouseUpWhileDragging?: () => void;
  onBoxClear?: () => void;
  onNewShapeCreated?: (rect: [number, number, number, number]) => void;

  // POG images props
  showPogs: boolean;
  currentPogs: CameraPog[];
  preloadedImagesB64: Record<string, string>;
  selectedIlItemIds: Set<number>;
  planogramStatusMap: Map<number, string>;
  onPogBoxClick: (bbox: PogBBox) => void;
  onPogBoxRightClick?: (bbox: ImageBox | PogBBox) => void;
  selectedComplianceLayers?: Set<string>;
  onToggleComplianceLayer?: (layer: string) => void;
}

export const CameraImageViewer: React.FC<CameraImageViewerProps> = (props) => {
  const {
    // Camera image props
    preloadedImage,
    fallbackPogBoxColor,
    filteredBboxes,
    highlightedIlItemId,
    boxActions,
    productRecognitionDetections,
    planogramComplianceActions,
    showProductImages,
    onBoxClick,
    onProductRecognitionClick,
    onProductRecognitionRightClick,
    titleActions,
    onBoxesSelected,

    // Edit mode props
    editMode,
    selectedShape,
    onEditModeEnable,
    onRemoveShape,
    onSaveChanges,
    onCancelChanges,
    onDetectionEdited,
    onMouseUpWhileDragging,
    onBoxClear,
    onNewShapeCreated,

    // POG images props
    showPogs,
    currentPogs,
    preloadedImagesB64,
    selectedIlItemIds,
    planogramStatusMap,
    onPlanogramComplianceClick,
    onPogBoxClick,
    onPogBoxRightClick,
    selectedComplianceLayers,
    onToggleComplianceLayer,
  } = props;
  const selectedPogBoxColor = props.selectedPogBoxColor || '#2563eb';

  // Decode image to get dimensions for DragOverlay
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    if (preloadedImage?.startsWith('data:')) {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.src = preloadedImage;
    }
  }, [preloadedImage]);

  return (
    <div
      className={`grid grid-cols-2 ${
        showPogs && currentPogs.length > 0 ? 'lg:grid-cols-3' : ''
      } gap-4`}
    >
      {/* Camera Image */}
      <div className="space-y-2">
        <div className="flex items-center justify-between min-h-10">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">Camera Images</h3>
            {editMode && selectedShape && (
              <button
                onClick={onRemoveShape}
                className="px-3 py-1 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700"
              >
                Remove Shape
              </button>
            )}
            {!editMode ? (
              <button
                onClick={onEditModeEnable}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  editMode
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Edit OD Layers
              </button>
            ) : (
              <>
                <button
                  onClick={onCancelChanges}
                  className="px-3 py-1 rounded text-sm font-medium bg-gray-500 text-white hover:bg-gray-600"
                >
                  Discard changes
                </button>
                <button
                  onClick={onSaveChanges}
                  className="px-3 py-1 rounded text-sm font-medium bg-green-600 text-white hover:bg-green-700"
                >
                  Save Changes
                </button>
              </>
            )}
          </div>
          {titleActions}
        </div>
        <div className="relative">
          <ImageViewer<BBox>
            imageBase64={preloadedImage?.startsWith('data:') ? preloadedImage : undefined}
            bboxes={filteredBboxes}
            onBoxClick={onBoxClick}
            onBoxRightClick={onProductRecognitionRightClick}
            boxActions={boxActions}
            productRecognitionDetections={productRecognitionDetections}
            onProductRecognitionClick={onProductRecognitionClick}
            enableZoom={true}
            showProductImages={showProductImages}
            onBoxesSelected={onBoxesSelected}
            editMode={editMode}
            selectedDetection={selectedShape}
            onDetectionEdited={onDetectionEdited}
            onMouseUpWhileDragging={onMouseUpWhileDragging}
            onBoxClear={onBoxClear}
            onNewShapeCreated={onNewShapeCreated}
            imageSrc={preloadedImage}
            imageDimensions={imageDimensions || undefined}
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="min-h-10"></div>
        <ImageViewer<BBox>
          imageBase64={preloadedImage?.startsWith('data:') ? preloadedImage : undefined}
          bboxes={[]}
          onBoxClick={() => {}}
          boxActions={[]}
          productRecognitionDetections={[]}
          planogramComplianceActions={planogramComplianceActions}
          enableZoom={true}
          showProductImages={showProductImages}
          onPlanogramComplianceClick={onPlanogramComplianceClick}
        />
      </div>

      {/* POG Images - only show if there are POGs and showPogs is enabled */}
      {showPogs && currentPogs.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between min-h-10">
            <h3 className="text-lg font-semibold">POG Images ({currentPogs.length})</h3>
            {/* Compliance Legend */}
            {selectedComplianceLayers && onToggleComplianceLayer && (
              <ComplianceLayerLegend
                selectedComplianceLayers={selectedComplianceLayers}
                planogramStatusMap={planogramStatusMap}
                onToggleComplianceLayer={onToggleComplianceLayer}
              />
            )}
          </div>
          <div className={`grid grid-cols-1 lg:grid-cols-${currentPogs.length || 1} gap-4`}>
            {currentPogs.map((pog) => {
              const preloadedPogImage = pog?.pogImageUri
                ? preloadedImagesB64[pog.pogImageUri]
                : undefined;

              // Map each pog bbox to include color based on compliance status
              const pogBboxesWithColor = (pog?.bboxes ?? []).map((bbox) => {
                let color = fallbackPogBoxColor ?? '#3b82f6'; // fallback color

                // Check if this POG item should be highlighted
                // Since both planogram and realogram clicks now use comprehensive selection,
                // we just need to check if this item is in the selected set
                let isSelected = false;
                if (selectedIlItemIds.size > 0) {
                  isSelected = selectedIlItemIds.has(bbox.ilItemId);
                }

                // If highlighted, use blue color. Otherwise, use compliance status color
                if (highlightedIlItemId && bbox.ilItemId === highlightedIlItemId) {
                  color = '#FFFF00';
                } else if (isSelected) {
                  color = selectedPogBoxColor;
                } else if (bbox.ilItemId && planogramStatusMap.has(bbox.ilItemId)) {
                  const status = planogramStatusMap.get(bbox.ilItemId);
                  if (status && status in COMPLIANCE_COLOR) {
                    color = COMPLIANCE_COLOR[status as keyof typeof COMPLIANCE_COLOR];
                  }
                }
                return { ...bbox, color };
              });

              return (
                <div key={`pog-${pog?.ilSectionId}`}>
                  {pog?.pogImageUri ? (
                    <ImageViewer<PogBBox>
                      imageBase64={
                        preloadedPogImage?.startsWith('data:') ? preloadedPogImage : undefined
                      }
                      bboxes={pogBboxesWithColor}
                      onBoxClick={onPogBoxClick}
                      onBoxRightClick={onPogBoxRightClick}
                      boxActions={[]}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-48 bg-gray-100 rounded">
                      <p className="text-gray-500 text-sm">No POG image available</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
